/**
 * ArrearsHistoryView — read-only historical view of the Loan Arrears
 * Ageing book as of a stored snapshot date. Rendered by LoanAgeing.jsx
 * when a past report is picked from the History selector.
 *
 * Data: one `collections_arrears_snapshots` doc (rows + versioned
 * `metrics` block computed at upload time). Rows older than ~12 months
 * are trimmed from Mongo — metrics survive forever and the raw file
 * lives in OneDrive.
 */
import { useState, useMemo } from "react";
import { cur, fmtMoney, isAusDesk, rl, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";
import {
  History, ExternalLink, FileSpreadsheet, Search, Flame, ArrowLeft,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const fmtGBP = (v) => fmtMoney(v, 0);

const BUCKET_LABELS = {
  "1_7": "1-7 days", "8_30": "8-30 days", "31_60": "31-60 days",
  "61_90": "61-90 days", "90_plus": "90+ days",
};
const BUCKET_TONES = {
  "1_7": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "8_30": "bg-amber-50 text-amber-800 border-amber-200",
  "31_60": "bg-orange-50 text-orange-800 border-orange-200",
  "61_90": "bg-rose-50 text-rose-800 border-rose-200",
  "90_plus": "bg-red-100 text-red-900 border-red-300",
};

export default function ArrearsHistoryView({ snapshot, onBack }) {
  const [search, setSearch] = useState("");
  const m = snapshot.metrics || {};
  const rows = useMemo(() => {
    const all = (snapshot.rows || []).slice().sort(
      (a, b) => (b.days_past_due || 0) - (a.days_past_due || 0),
    );
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(r =>
      (r.customer_name || "").toLowerCase().includes(q)
      || (r.sf_number || "").toLowerCase().includes(q)
      || (r.loan_application_id || "").toLowerCase().includes(q),
    );
  }, [snapshot.rows, search]);

  const uploadedAt = snapshot.uploaded_at
    ? new Date(snapshot.uploaded_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="space-y-3" data-testid="arrears-history-view">
      {/* Historical banner */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-indigo-900 min-w-0 flex-wrap">
          <History className="w-4 h-4 shrink-0" />
          <span className="font-semibold whitespace-nowrap">Historical snapshot — {uploadedAt}</span>
          <span className="text-indigo-400">·</span>
          <span className="flex items-center gap-1 text-xs text-indigo-700 min-w-0">
            <FileSpreadsheet className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate max-w-[240px]" title={snapshot.filename}>{snapshot.filename}</span>
          </span>
          {snapshot.onedrive?.status === "uploaded" && snapshot.onedrive.web_url && (
            <a
              href={snapshot.onedrive.web_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline whitespace-nowrap"
              data-testid="arrears-history-onedrive-link"
            >
              <ExternalLink className="w-3 h-3" /> Raw file on OneDrive
            </a>
          )}
          <span className="text-[11px] text-indigo-600 italic whitespace-nowrap">Read-only — actions disabled</span>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={onBack} data-testid="arrears-history-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to today
        </Button>
      </div>

      {/* KPI strip from the snapshot metrics */}
      <div className="flex items-center gap-4 flex-wrap text-sm" data-testid="arrears-history-kpis">
        <Kpi label="Cases" value={m.row_count ?? (snapshot.totals?.row_count || 0)} />
        <Kpi label="Arrears" value={fmtGBP(m.total_arrears ?? snapshot.totals?.arrears_amount)} tone="text-rose-700" />
        <Kpi label="Balance" value={fmtGBP(m.total_balance ?? snapshot.totals?.balance_amount)} />
        <Kpi label="Principal out" value={fmtGBP(m.total_principal_outstanding ?? snapshot.totals?.principal_outstanding)} />
        {m.dpd && (
          <>
            <Kpi label="Avg DPD" value={m.dpd.avg} />
            <Kpi label="Median DPD" value={m.dpd.median} />
            <Kpi label="Max DPD" value={m.dpd.max} tone="text-red-700" />
          </>
        )}
      </div>

      {/* DPD buckets as of that date */}
      {m.dpd_buckets && (
        <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="arrears-history-buckets">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">
            <Flame className="w-3 h-3 text-amber-600" /> DPD
          </span>
          {Object.entries(BUCKET_LABELS).map(([key, label]) => {
            const b = m.dpd_buckets[key];
            if (!b) return null;
            return (
              <span key={key} className={`px-2 h-6 inline-flex items-center gap-1.5 rounded-md text-[11px] font-medium border ${BUCKET_TONES[key]}`}>
                {label}
                <b className="tabular-nums">{b.count}</b>
                <span className="opacity-70 tabular-nums">{fmtGBP(b.arrears)}</span>
              </span>
            );
          })}
        </div>
      )}

      {/* Case table (as reported that day) */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 p-2 border-b">
            <Search className="w-4 h-4 text-muted-foreground shrink-0 ml-1" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer or SF# in this snapshot…"
              className="h-8 text-xs border-0 shadow-none focus-visible:ring-0"
              data-testid="arrears-history-search"
            />
            <span className="text-[11px] text-muted-foreground whitespace-nowrap pr-2">
              {rows.length} of {(snapshot.rows || []).length}
            </span>
          </div>
          {(snapshot.rows || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {snapshot.rows_trimmed
                ? "Row detail for this snapshot has been trimmed (we keep ~12 months of full detail). Totals and metrics above are permanent — the raw file is on OneDrive."
                : "No rows stored for this snapshot."}
            </div>
          ) : (
            <div className="overflow-auto max-h-[62vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-3 py-2 font-semibold">Customer</th>
                    <th className="text-left px-3 py-2 font-semibold">SF#</th>
                    <th className="text-center px-3 py-2 font-semibold">DPD</th>
                    <th className="text-right px-3 py-2 font-semibold">Arrears {cur()}</th>
                    <th className="text-right px-3 py-2 font-semibold">Principal out {cur()}</th>
                    <th className="text-right px-3 py-2 font-semibold">Total out {cur()}</th>
                    <th className="text-left px-3 py-2 font-semibold">Status (LoanDisk)</th>
                    <th className="text-left px-3 py-2 font-semibold">Handler (at time)</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-testid="arrears-history-table">
                  {rows.map((r, i) => (
                    <tr key={r.loandisk_loan_id || i} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.customer_name || "—"}</div>
                        {r.borrower_director_name && (
                          <div className="text-[10px] text-muted-foreground">{r.borrower_director_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.loan_application_id || r.sf_number || "—"}</td>
                      <td className="px-3 py-2 text-center tabular-nums font-semibold">{r.days_past_due ?? "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-medium">{fmtGBP(r.arrears_amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtGBP(r.total_principal_outstanding)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtGBP(r.balance_amount)}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">{r.loandisk_status_text || "—"}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.handler || <span className="text-muted-foreground italic">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, tone = "text-slate-900" }) {
  return (
    <span className="whitespace-nowrap">
      <span className={`font-bold tabular-nums ${tone}`}>{value}</span>
      <span className="text-[11px] text-muted-foreground ml-1">{label}</span>
    </span>
  );
}
